package fr.frux.feature.treedetail.identity

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.CertaintyCard
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxNoteField
import fr.frux.core.designsystem.components.FruxSearchField
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.icons.legendColor
import fr.frux.core.designsystem.icons.speciesFamilyKey
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.treedetail.TreeDetail
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_certain
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_certain_description
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_certainty_title
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_current
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_doubt
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_doubt_withdraw
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_probable
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_probable_description
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_reason_label
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_reason_placeholder
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_remove
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_save
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_search
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_search_clear
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_species_label
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_species_title
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_to_confirm
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_to_confirm_description
import fr.frux.shared.content.TreeCorrectionLimits
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private const val MAX_RESULTS = 8
private val DISC = 28.dp

/** Doute d'identification offert au pied de la feuille d'espèce. */
enum class DoubtAction { Report, Withdraw }

/** Rien sur son propre arbre, « Retirer mon doute » si on a déjà signalé, sinon le signalement. */
fun doubtAction(tree: TreeDetail): DoubtAction? =
    when {
        tree.isOwnTree -> null
        tree.reportedByMe -> DoubtAction.Withdraw
        else -> DoubtAction.Report
    }

internal fun Certainty.titleRes(): StringResource =
    when (this) {
        Certainty.CERTAIN -> Res.string.treedetail_identity_sheet_certain
        Certainty.PROBABLE -> Res.string.treedetail_identity_sheet_probable
        Certainty.TO_CONFIRM -> Res.string.treedetail_identity_sheet_to_confirm
    }

/** « Poirier · Probable » : l'espèce et la certitude courantes de la fiche. */
@Composable
internal fun currentIdentityLabel(tree: TreeDetail): String =
    "${tree.speciesName} · ${stringResource(tree.certainty.titleRes())}"

/** Disque d'espèce de la carte (glyphe et teinte de famille), repli sur la fleur. */
@Composable
internal fun SpeciesDisc(
    scientificName: String,
    harvestPart: String?,
    size: Dp,
) {
    TreeGlyphDisc(
        glyph = glyphFor(scientificName, harvestPart) ?: FruxSpeciesGlyphs.Fleur,
        tint = speciesFamilyKey(scientificName).legendColor(MaterialTheme.fruxColors),
        size = size,
    )
}

@Composable
private fun Species.Disc() = SpeciesDisc(scientificName, harvests.firstOrNull()?.part?.name, DISC)

/** Feuille « Corriger l'espèce » (canvas `CorrigerEspece`). */
@Composable
internal fun CorrectSpeciesSheet(
    sheet: IdentitySheet.Species,
    current: String,
    controller: TreeIdentityController,
    doubt: DoubtAction?,
    onDoubt: () -> Unit,
) {
    IdentitySheetLayout(
        title = stringResource(Res.string.treedetail_identity_sheet_species_title),
        subtitle = stringResource(Res.string.treedetail_identity_sheet_current, current),
        onClose = controller::dismissSheet,
        footer = { SpeciesFooter(sheet, controller, doubt, onDoubt) },
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                text = stringResource(Res.string.treedetail_identity_sheet_species_label),
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurface,
            )
            SpeciesField(sheet, controller)
        }
        CertaintyChoices(sheet.certainty, controller::selectCertainty)
        FruxNoteField(
            value = sheet.reason,
            onValueChange = controller::onSpeciesReasonChange,
            label = stringResource(Res.string.treedetail_identity_sheet_reason_label),
            placeholder = stringResource(Res.string.treedetail_identity_sheet_reason_placeholder),
            maxLength = TreeCorrectionLimits.REASON_MAX,
            showCounter = false,
            minLines = 2,
            labelStyle = MaterialTheme.typography.bodySmall,
        )
    }
}

/** Pastille de l'espèce retenue, sinon champ de recherche et jusqu'à huit résultats. */
@Composable
private fun SpeciesField(
    sheet: IdentitySheet.Species,
    controller: TreeIdentityController,
) {
    val chosen = sheet.species
    if (chosen != null) {
        Surface(
            modifier = Modifier.fillMaxWidth().heightIn(min = 56.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline),
        ) {
            Row(
                modifier = Modifier.padding(start = 16.dp, end = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Search,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                ChosenChip(chosen, onRemove = controller::clearSpecies)
            }
        }
        return
    }
    FruxSearchField(
        value = sheet.query,
        onValueChange = controller::onSpeciesQueryChange,
        placeholder = stringResource(Res.string.treedetail_identity_sheet_search),
        clearLabel = stringResource(Res.string.treedetail_identity_sheet_search_clear),
    )
    sheet.results.take(MAX_RESULTS).forEach { species ->
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .heightIn(min = 48.dp)
                    .clickable(role = Role.Button) { controller.selectSpecies(species) }
                    .padding(horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            species.Disc()
            Text(
                text = species.commonNameFr,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

@Composable
private fun ChosenChip(
    species: Species,
    onRemove: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .clip(RoundedCornerShape(50))
                .background(MaterialTheme.colorScheme.secondaryContainer)
                .heightIn(min = 40.dp)
                .padding(start = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        species.Disc()
        Text(
            text = species.commonNameFr,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
        )
        Box(
            modifier = Modifier.size(40.dp).clickable(role = Role.Button, onClick = onRemove),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = Icons.Outlined.Close,
                contentDescription = stringResource(Res.string.treedetail_identity_sheet_remove, species.commonNameFr),
                modifier = Modifier.size(18.dp),
                tint = MaterialTheme.colorScheme.onSecondaryContainer,
            )
        }
    }
}

@Composable
private fun CertaintyChoices(
    selected: Certainty,
    onSelect: (Certainty) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = stringResource(Res.string.treedetail_identity_sheet_certainty_title),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.onSurface,
        )
        Column(modifier = Modifier.selectableGroup(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            CertaintyCard(
                title = stringResource(Res.string.treedetail_identity_sheet_certain),
                description = stringResource(Res.string.treedetail_identity_sheet_certain_description),
                bars = 3,
                selected = selected == Certainty.CERTAIN,
                onSelect = { onSelect(Certainty.CERTAIN) },
            )
            CertaintyCard(
                title = stringResource(Res.string.treedetail_identity_sheet_probable),
                description = stringResource(Res.string.treedetail_identity_sheet_probable_description),
                bars = 2,
                selected = selected == Certainty.PROBABLE,
                onSelect = { onSelect(Certainty.PROBABLE) },
            )
            CertaintyCard(
                title = stringResource(Res.string.treedetail_identity_sheet_to_confirm),
                description = stringResource(Res.string.treedetail_identity_sheet_to_confirm_description),
                bars = 1,
                selected = selected == Certainty.TO_CONFIRM,
                onSelect = { onSelect(Certainty.TO_CONFIRM) },
            )
        }
    }
}

@Composable
private fun SpeciesFooter(
    sheet: IdentitySheet.Species,
    controller: TreeIdentityController,
    doubt: DoubtAction?,
    onDoubt: () -> Unit,
) {
    sheet.error?.let {
        Text(
            text = it.userMessage(),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.error,
        )
    }
    FruxButton(
        text = stringResource(Res.string.treedetail_identity_sheet_save),
        onClick = controller::saveSpecies,
        enabled = sheet.canSave,
        loading = sheet.saving,
        modifier = Modifier.fillMaxWidth(),
    )
    if (doubt != null) {
        val label =
            when (doubt) {
                DoubtAction.Report -> stringResource(Res.string.treedetail_identity_sheet_doubt)
                DoubtAction.Withdraw -> stringResource(Res.string.treedetail_identity_sheet_doubt_withdraw)
            }
        FruxButton(
            text = label,
            onClick = onDoubt,
            style = FruxButtonStyle.Text,
            icon = Icons.Outlined.Flag,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}
